import { useState, useEffect, useCallback } from "react";

const RESTAURANT_LAT = 41.3781;
const RESTAURANT_LNG = 2.1445;
const NEAR_RADIUS_KM = 15;

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export type GeoStatus = "idle" | "loading" | "nearby" | "far" | "denied";

interface GeoData {
  status: GeoStatus;
  distanceKm: number | null;
}

export const useGeolocation = (): GeoData & { requestLocation: () => void } => {
  const [data, setData] = useState<GeoData>({ status: "idle", distanceKm: null });
  const [requested, setRequested] = useState(false);

  const requestLocation = useCallback(() => {
    setRequested(true);
  }, []);

  useEffect(() => {
    if (!requested) return;
    if (!navigator.geolocation) {
      setData({ status: "far", distanceKm: null });
      return;
    }

    setData({ status: "loading", distanceKm: null });

    const timeout = setTimeout(() => {
      setData((prev) => (prev.status === "loading" ? { status: "far", distanceKm: null } : prev));
    }, 5000);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timeout);
        const dist = haversineDistance(pos.coords.latitude, pos.coords.longitude, RESTAURANT_LAT, RESTAURANT_LNG);
        setData({
          status: dist <= NEAR_RADIUS_KM ? "nearby" : "far",
          distanceKm: Math.round(dist * 10) / 10,
        });
      },
      () => {
        clearTimeout(timeout);
        setData({ status: "far", distanceKm: null });
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );

    return () => clearTimeout(timeout);
  }, [requested]);

  return { ...data, requestLocation };
};
